import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem } from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Loader2, MessageSquare, Clock, User, Calendar, Search, Filter, Plus, X, Edit, Send } from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import DateRangePicker from '@/components/portal/DateRangePicker';

// Helper function to convert unix timestamp to Date
const convertUnixToDate = (timestamp) => {
  if (!timestamp) return null;
  const dateInMs = timestamp < 10000000000 ? timestamp * 1000 : timestamp;
  return new Date(dateInMs);
};

// Helper function to format date for display
const formatDisplayDate = (timestamp) => {
  const date = convertUnixToDate(timestamp);
  if (!date) return 'N/A';
  try {
    return format(date, 'MMM dd, yyyy HH:mm');
  } catch {
    return 'N/A';
  }
};

export default function MyTickets({ userInfo }) {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [error, setError] = useState('');
  
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [searchExpanded, setSearchExpanded] = useState(false);
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [selectedPriorities, setSelectedPriorities] = useState([]);
  const [dateRange, setDateRange] = useState(null);
  
  // Create ticket modal
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showSummaryModal, setShowSummaryModal] = useState(false);
  const [newTicket, setNewTicket] = useState({ name: '', description: '' });
  const [creating, setCreating] = useState(false);
  
  // Edit ticket modal
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingTicket, setEditingTicket] = useState(null);
  const [editFormData, setEditFormData] = useState({ name: '', description: '' });
  const [updating, setUpdating] = useState(false);
  
  // Add comment
  const [newComment, setNewComment] = useState('');
  const [addingComment, setAddingComment] = useState(false);
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  useEffect(() => {
    fetchTickets();
  }, []);

  const fetchTickets = async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await axios.get('https://api1.simplyworkcrm.com/api:JBYRWqH3/support/mytickets', {
        params: {
          user_id: userInfo.user_id
        }
      });
      
      setTickets(response.data || []);
      
      // Auto-select first ticket if available
      if (response.data && response.data.length > 0) {
        setSelectedTicket(response.data[0]);
      }
    } catch (err) {
      console.error('Error fetching tickets:', err);
      setError('Failed to load tickets. Please try again.');
      toast.error('Failed to load tickets');
    } finally {
      setLoading(false);
    }
  };

  // Get unique statuses from tickets
  const uniqueStatuses = [...new Set(tickets.map(t => t.status).filter(Boolean))];
  
  // Get unique priorities from tickets
  const uniquePriorities = [...new Set(tickets.map(t => t.priority).filter(Boolean))];

  // Filter tickets based on search, status, priority, and date
  const filteredTickets = tickets.filter(ticket => {
    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const matchesSearch = 
        ticket.name?.toLowerCase().includes(query) ||
        ticket.description?.toLowerCase().includes(query) ||
        ticket.assignedTo_name?.toLowerCase().includes(query);
      if (!matchesSearch) return false;
    }
    
    // Status filter
    if (selectedStatuses.length > 0 && !selectedStatuses.includes(ticket.status)) {
      return false;
    }
    
    // Priority filter
    if (selectedPriorities.length > 0 && !selectedPriorities.includes(ticket.priority)) {
      return false;
    }
    
    // Date range filter
    if (dateRange && dateRange.from && dateRange.to) {
      const ticketDate = convertUnixToDate(ticket.created_at);
      if (!ticketDate) return false;
      
      ticketDate.setHours(0, 0, 0, 0);
      const startDate = new Date(dateRange.from);
      startDate.setHours(0, 0, 0, 0);
      const endDate = new Date(dateRange.to);
      endDate.setHours(23, 59, 59, 999);
      
      if (ticketDate < startDate || ticketDate > endDate) return false;
    }
    
    return true;
  });

  const handleCreateTicket = () => {
    if (!newTicket.name.trim()) {
      toast.error('Subject is required');
      return;
    }
    
    // Show summary modal for confirmation
    setShowCreateModal(false);
    setShowSummaryModal(true);
  };

  const handleConfirmCreate = async () => {
    setCreating(true);
    try {
      const response = await axios.post(
        'https://api1.simplyworkcrm.com/api:JBYRWqH3/support/mytickets/add',
        {
          user_id: userInfo.user_id,
          name: newTicket.name,
          description: newTicket.description
        }
      );
      
      if (response.data) {
        toast.success('Ticket created successfully!');
        setShowSummaryModal(false);
        setNewTicket({ name: '', description: '' });
        fetchTickets();
      }
    } catch (error) {
      console.error('Error creating ticket:', error);
      toast.error(error.response?.data?.message || 'Failed to create ticket');
    } finally {
      setCreating(false);
    }
  };

  const handleEditTicket = (ticket) => {
    setEditingTicket(ticket);
    setEditFormData({
      name: ticket.name,
      description: ticket.description || ''
    });
    setShowEditModal(true);
  };

  const handleUpdateTicket = async () => {
    if (!editFormData.name.trim()) {
      toast.error('Subject is required');
      return;
    }

    setUpdating(true);
    try {
      const response = await axios.post(
        'https://api1.simplyworkcrm.com/api:JBYRWqH3/support/mytickets/update',
        {
          ticket_id: editingTicket.id,
          user_id: userInfo.user_id,
          name: editFormData.name,
          description: editFormData.description
        }
      );
      
      if (response.data) {
        toast.success('Ticket updated successfully!');
        setShowEditModal(false);
        setEditingTicket(null);
        setEditFormData({ name: '', description: '' });
        fetchTickets();
      }
    } catch (error) {
      console.error('Error updating ticket:', error);
      toast.error(error.response?.data?.message || 'Failed to update ticket');
    } finally {
      setUpdating(false);
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) {
      toast.error('Comment cannot be empty');
      return;
    }

    setAddingComment(true);
    try {
      const response = await axios.post(
        'https://api1.simplyworkcrm.com/api:JBYRWqH3/support/mytickets/comment/add',
        {
          ticket_id: selectedTicket.id,
          user_id: userInfo.user_id,
          message: newComment
        }
      );
      
      if (response.data) {
        toast.success('Comment added successfully!');
        setNewComment('');
        fetchTickets();
      }
    } catch (error) {
      console.error('Error adding comment:', error);
      toast.error(error.response?.data?.message || 'Failed to add comment');
    } finally {
      setAddingComment(false);
    }
  };

  const clearFilters = () => {
    setSearchQuery('');
    setSelectedStatuses([]);
    setSelectedPriorities([]);
    setDateRange(null);
    setSearchExpanded(false);
    setCurrentPage(1); // Reset to first page when clearing filters
  };
  
  // Paginate filtered tickets
  const paginatedTickets = filteredTickets.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );
  const totalPages = Math.ceil(filteredTickets.length / itemsPerPage);

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'to do':
        return 'bg-slate-50 text-slate-700 border border-slate-200';
      case 'in progress':
        return 'bg-sky-50 text-sky-700 border border-sky-200';
      case 'blocked':
        return 'bg-rose-50 text-rose-700 border border-rose-200';
      case 'completed':
        return 'bg-emerald-50 text-emerald-700 border border-emerald-200';
      default:
        return 'bg-neutral-50 text-neutral-600 border border-neutral-200';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority?.toLowerCase()) {
      case 'high':
        return 'bg-rose-50 text-rose-600 border border-rose-200';
      case 'intermediate':
        return 'bg-amber-50 text-amber-600 border border-amber-200';
      case 'low':
        return 'bg-slate-50 text-slate-600 border border-slate-200';
      default:
        return 'bg-neutral-50 text-neutral-600 border border-neutral-200';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-[#d4af37]" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-96">
        <p className="text-red-600 mb-4">{error}</p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Modern Header */}
      <div>
        <h2 className="text-4xl font-semibold text-neutral-900 tracking-tight">My Tickets</h2>
        <p className="text-neutral-500 mt-2 text-sm">Manage and track your support requests</p>
      </div>

      <div className="grid grid-cols-12 gap-6">
        {/* Left Side - Tickets List */}
        <div className="col-span-5">
          <div className="bg-white rounded-2xl border border-neutral-100 overflow-hidden shadow-sm">
            <div className="px-4 py-3 border-b border-neutral-100">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h3 className="text-base font-semibold text-neutral-900">All Tickets</h3>
                  <p className="text-xs text-neutral-500 mt-0.5">{filteredTickets.length} of {tickets.length} tickets</p>
                </div>
                <Button
                  onClick={() => setShowCreateModal(true)}
                  className="bg-gradient-to-r from-[#d4af37] to-[#b8941e] hover:from-[#b8941e] hover:to-[#d4af37] text-white rounded-xl px-3 py-1.5 text-sm shadow-sm"
                >
                  <Plus className="w-3.5 h-3.5 mr-1.5" />
                  Create Ticket
                </Button>
              </div>
              
              {/* Filters Row */}
              <div className="flex items-center gap-2">
                {/* Search */}
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className={`flex items-center transition-all duration-300 ${searchExpanded ? 'flex-1' : ''}`}>
                        {!searchExpanded ? (
                          <Button
                            variant="outline"
                            size="icon"
                            onClick={() => setSearchExpanded(true)}
                            className="rounded-xl h-9 w-9"
                          >
                            <Search className="w-4 h-4" />
                          </Button>
                        ) : (
                          <div className="flex items-center gap-2 flex-1">
                            <div className="relative flex-1">
                              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-neutral-400" />
                              <Input
                                placeholder="Search tickets..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="pl-10 rounded-xl h-9 text-sm"
                                autoFocus
                              />
                            </div>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => {
                                setSearchQuery('');
                                setSearchExpanded(false);
                              }}
                              className="rounded-xl h-9 w-9"
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        )}
                      </div>
                    </TooltipTrigger>
                    {!searchExpanded && (
                      <TooltipContent><p>Quick Search</p></TooltipContent>
                    )}
                  </Tooltip>
                </TooltipProvider>

                {/* Status Filter */}
                {uniqueStatuses.length > 0 && (
                  <TooltipProvider>
                    <Tooltip>
                      <Popover>
                        <PopoverTrigger asChild>
                          <TooltipTrigger asChild>
                            <Button
                              variant="outline"
                              size="icon"
                              className={`rounded-xl h-9 w-9 relative ${selectedStatuses.length > 0 ? 'border-[#d4af37] bg-[#f4e5b8]' : ''}`}
                            >
                              <Filter className="w-4 h-4" />
                              {selectedStatuses.length > 0 && (
                                <span className="absolute -top-1 -right-1 bg-[#d4af37] text-white text-[9px] rounded-full w-4 h-4 flex items-center justify-center">
                                  {selectedStatuses.length}
                                </span>
                              )}
                            </Button>
                          </TooltipTrigger>
                        </PopoverTrigger>
                        <PopoverContent className="w-56 p-0" align="start">
                          <Command>
                            <CommandInput placeholder="Search status..." />
                            <CommandEmpty>No status found.</CommandEmpty>
                            <CommandGroup>
                              {uniqueStatuses.map((status) => (
                                <CommandItem
                                  key={status}
                                  onSelect={() => {
                                    setSelectedStatuses(prev =>
                                      prev.includes(status)
                                        ? prev.filter(s => s !== status)
                                        : [...prev, status]
                                    );
                                  }}
                                >
                                  <Checkbox
                                    checked={selectedStatuses.includes(status)}
                                    className="mr-2"
                                  />
                                  {status}
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          </Command>
                        </PopoverContent>
                      </Popover>
                      <TooltipContent><p>By Status</p></TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}

                {/* Priority Filter */}
                {uniquePriorities.length > 0 && (
                  <TooltipProvider>
                    <Tooltip>
                      <Popover>
                        <PopoverTrigger asChild>
                          <TooltipTrigger asChild>
                            <Button
                              variant="outline"
                              size="icon"
                              className={`rounded-xl h-9 w-9 relative ${selectedPriorities.length > 0 ? 'border-[#d4af37] bg-[#f4e5b8]' : ''}`}
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M12 2L2 7l10 5 10-5-10-5z"/>
                                <path d="M2 17l10 5 10-5"/>
                                <path d="M2 12l10 5 10-5"/>
                              </svg>
                              {selectedPriorities.length > 0 && (
                                <span className="absolute -top-1 -right-1 bg-[#d4af37] text-white text-[9px] rounded-full w-4 h-4 flex items-center justify-center">
                                  {selectedPriorities.length}
                                </span>
                              )}
                            </Button>
                          </TooltipTrigger>
                        </PopoverTrigger>
                        <PopoverContent className="w-56 p-0" align="start">
                          <Command>
                            <CommandInput placeholder="Search priority..." />
                            <CommandEmpty>No priority found.</CommandEmpty>
                            <CommandGroup>
                              {uniquePriorities.map((priority) => (
                                <CommandItem
                                  key={priority}
                                  onSelect={() => {
                                    setSelectedPriorities(prev =>
                                      prev.includes(priority)
                                        ? prev.filter(p => p !== priority)
                                        : [...prev, priority]
                                    );
                                  }}
                                >
                                  <Checkbox
                                    checked={selectedPriorities.includes(priority)}
                                    className="mr-2"
                                  />
                                  {priority}
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          </Command>
                        </PopoverContent>
                      </Popover>
                      <TooltipContent><p>By Priority</p></TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}

                {/* Date Range Filter */}
                <TooltipProvider>
                  <Tooltip>
                    <Popover>
                      <PopoverTrigger asChild>
                        <TooltipTrigger asChild>
                          <Button
                            variant="outline"
                            size="icon"
                            className={`rounded-xl h-9 w-9 ${dateRange ? 'border-[#d4af37] bg-[#f4e5b8]' : ''}`}
                          >
                            <Calendar className="w-4 h-4" />
                          </Button>
                        </TooltipTrigger>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-4" align="start">
                        <div className="space-y-3">
                          <div className="flex items-center justify-between">
                            <p className="text-sm font-medium text-neutral-700">Date Created</p>
                            {dateRange && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setDateRange(null)}
                                className="h-6 text-xs"
                              >
                                Clear
                              </Button>
                            )}
                          </div>
                          <DateRangePicker value={dateRange} onChange={setDateRange} />
                        </div>
                      </PopoverContent>
                    </Popover>
                    <TooltipContent><p>By Date Created</p></TooltipContent>
                  </Tooltip>
                </TooltipProvider>

                {/* Clear All Filters */}
                {(searchQuery || selectedStatuses.length > 0 || selectedPriorities.length > 0 || dateRange) && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={clearFilters}
                    className="text-xs text-red-600 hover:text-red-700 hover:bg-red-50 rounded-xl h-9 px-3"
                  >
                    Clear All
                  </Button>
                )}
              </div>
            </div>
            <div className="p-2">
              {filteredTickets.length === 0 ? (
                <div className="text-center py-12">
                  <div className="w-12 h-12 bg-neutral-50 rounded-xl flex items-center justify-center mx-auto mb-3">
                    <MessageSquare className="w-6 h-6 text-neutral-300" />
                  </div>
                  <p className="text-neutral-400 text-sm">
                    {tickets.length === 0 ? 'No tickets found' : 'No tickets match your filters'}
                  </p>
                </div>
              ) : (
                <>
                  <div className="space-y-1.5 max-h-[calc(100vh-380px)] overflow-y-auto pr-1">
                    {paginatedTickets.map((ticket) => (
                      <div
                        key={ticket.id}
                        className={`rounded-xl transition-all duration-200 ${
                          selectedTicket?.id === ticket.id 
                            ? 'bg-gradient-to-br from-[#f4e5b8] to-[#fef8e7] border-2 border-[#e8d4a0] shadow-sm' 
                            : 'border border-transparent hover:border-neutral-100'
                        }`}
                      >
                        <button
                          onClick={() => setSelectedTicket(ticket)}
                          className="w-full text-left p-3"
                        >
                          <div className="space-y-2">
                            {/* Title and Status */}
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-0.5">
                                  <h3 className={`font-medium text-sm line-clamp-1 ${
                                    selectedTicket?.id === ticket.id ? 'text-[#8b7435]' : 'text-neutral-900'
                                  }`}>
                                    {ticket.name || 'Untitled Ticket'}
                                  </h3>
                                  {ticket.ticket_reference && (
                                    <span className="text-[9px] px-1.5 py-0.5 bg-neutral-200 text-neutral-600 rounded font-mono font-medium shrink-0">
                                      #{ticket.ticket_reference}
                                    </span>
                                  )}
                                </div>
                              </div>
                              <div className="flex items-center gap-1.5 shrink-0">
                                {ticket.status && (
                                  <span className={`text-[9px] px-2 py-0.5 rounded-md font-medium uppercase tracking-wider ${
                                    selectedTicket?.id === ticket.id 
                                      ? 'bg-white/70 text-[#8b7435]' 
                                      : getStatusColor(ticket.status)
                                  }`}>
                                    {ticket.status}
                                  </span>
                                )}
                                {ticket.status?.toLowerCase() === 'to do' && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleEditTicket(ticket);
                                    }}
                                    className="p-1 hover:bg-white/50 rounded-md transition-colors"
                                    title="Edit ticket"
                                  >
                                    <Edit className="w-3 h-3 text-[#8b7435]" />
                                  </button>
                                )}
                              </div>
                            </div>
                            
                            {/* Description Preview */}
                            {ticket.description && (
                              <p className={`text-xs line-clamp-1 ${
                                selectedTicket?.id === ticket.id ? 'text-[#9c823e]' : 'text-neutral-500'
                              }`}>
                                {ticket.description}
                              </p>
                            )}
                            
                            {/* Meta Info and Badges in one row */}
                            <div className="flex items-center justify-between gap-2">
                              <div className="flex items-center gap-1.5 text-[10px]">
                                <Clock className={`w-3 h-3 ${
                                  selectedTicket?.id === ticket.id ? 'text-[#9c823e]' : 'text-neutral-400'
                                }`} />
                                <span className={selectedTicket?.id === ticket.id ? 'text-[#9c823e]' : 'text-neutral-500'}>
                                  {formatDisplayDate(ticket.created_at)}
                                </span>
                              </div>
                              
                              <div className="flex items-center gap-1.5 flex-wrap">
                                {ticket.priority && (
                                  <span className={`text-[9px] px-1.5 py-0.5 rounded-md font-medium uppercase tracking-wider ${
                                    selectedTicket?.id === ticket.id 
                                      ? 'bg-white/70 text-[#8b7435]' 
                                      : getPriorityColor(ticket.priority)
                                  }`}>
                                    {ticket.priority}
                                  </span>
                                )}
                                {ticket.comment && ticket.comment.length > 0 && (
                                  <span className={`text-[9px] px-1.5 py-0.5 rounded-md font-medium flex items-center gap-0.5 ${
                                    selectedTicket?.id === ticket.id 
                                      ? 'bg-white/70 text-[#8b7435]' 
                                      : 'bg-neutral-50 text-neutral-600'
                                  }`}>
                                    <MessageSquare className="w-2.5 h-2.5" />
                                    {ticket.comment.length}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        </button>
                      </div>
                    ))}
                  </div>
                  
                  {/* Pagination Controls */}
                  {totalPages > 1 && (
                    <div className="flex items-center justify-between px-2 py-3 border-t border-neutral-100 mt-2">
                      <p className="text-xs text-neutral-500">
                        Showing {(currentPage - 1) * itemsPerPage + 1}-{Math.min(currentPage * itemsPerPage, filteredTickets.length)} of {filteredTickets.length}
                      </p>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                          disabled={currentPage === 1}
                          className="h-7 px-2 text-xs rounded-lg"
                        >
                          Previous
                        </Button>
                        <div className="flex items-center gap-1">
                          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                            let pageNum;
                            if (totalPages <= 5) {
                              pageNum = i + 1;
                            } else if (currentPage <= 3) {
                              pageNum = i + 1;
                            } else if (currentPage >= totalPages - 2) {
                              pageNum = totalPages - 4 + i;
                            } else {
                              pageNum = currentPage - 2 + i;
                            }
                            return (
                              <Button
                                key={pageNum}
                                variant={currentPage === pageNum ? "default" : "outline"}
                                size="sm"
                                onClick={() => setCurrentPage(pageNum)}
                                className={`h-7 w-7 p-0 text-xs rounded-lg ${
                                  currentPage === pageNum 
                                    ? 'bg-gradient-to-r from-[#d4af37] to-[#b8941e] text-white border-0' 
                                    : ''
                                }`}
                              >
                                {pageNum}
                              </Button>
                            );
                          })}
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                          disabled={currentPage === totalPages}
                          className="h-7 px-2 text-xs rounded-lg"
                        >
                          Next
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        {/* Right Side - Ticket Details & Comments */}
        <div className="col-span-7">
          {selectedTicket ? (
            <div className="bg-white rounded-2xl border border-neutral-100 overflow-hidden shadow-sm">
              {/* Modern Header */}
              <div className="px-5 py-4 border-b border-neutral-100 bg-gradient-to-br from-neutral-50 to-white">
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h2 className="text-xl font-semibold text-neutral-900 leading-tight">
                        {selectedTicket.name || 'Untitled Ticket'}
                      </h2>
                      {selectedTicket.ticket_reference && (
                        <span className="text-[10px] px-2 py-0.5 bg-neutral-200 text-neutral-700 rounded font-mono font-semibold shrink-0">
                          #{selectedTicket.ticket_reference}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                
                {/* Badges Row */}
                <div className="flex items-center gap-2 flex-wrap mb-2">
                  {selectedTicket.status && (
                    <span className={`text-[9px] px-2 py-1 rounded-md font-medium uppercase tracking-wider ${getStatusColor(selectedTicket.status)}`}>
                      {selectedTicket.status}
                    </span>
                  )}
                  {selectedTicket.priority && (
                    <span className={`text-[9px] px-2 py-1 rounded-md font-medium uppercase tracking-wider ${getPriorityColor(selectedTicket.priority)}`}>
                      {selectedTicket.priority}
                    </span>
                  )}
                  {selectedTicket.assignedTo_name && (
                    <span className="text-[9px] px-2 py-1 rounded-md font-medium uppercase tracking-wider bg-gradient-to-br from-[#d4af37] to-[#b8941e] text-white flex items-center gap-1">
                      <User className="w-2.5 h-2.5" />
                      {selectedTicket.assignedTo_name}
                    </span>
                  )}
                </div>
                
                {/* Meta Info */}
                <div className="flex items-center gap-1.5 text-xs text-neutral-500">
                  <Calendar className="w-3.5 h-3.5" />
                  <span>Created {formatDisplayDate(selectedTicket.created_at)}</span>
                </div>
              </div>
              
              <div className="p-5 space-y-5">
                {/* Description Section */}
                <div>
                  <h3 className="text-xs font-semibold text-neutral-400 uppercase tracking-wider mb-2">Description</h3>
                  <div className="bg-neutral-50 rounded-xl p-4 border border-neutral-100">
                    <p className="text-sm text-neutral-700 leading-relaxed whitespace-pre-wrap">
                      {selectedTicket.description || 'No description provided'}
                    </p>
                  </div>
                </div>

                {/* Comments Section */}
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <h3 className="text-xs font-semibold text-neutral-400 uppercase tracking-wider">Comments</h3>
                    <span className="text-xs text-neutral-400">({selectedTicket.comment?.length || 0})</span>
                  </div>
                  
                  {selectedTicket.comment && selectedTicket.comment.length > 0 ? (
                    <div className="space-y-2 max-h-[280px] overflow-y-auto pr-1 mb-3">
                      {selectedTicket.comment.map((comment, index) => (
                        <div 
                          key={index}
                          className="bg-white border border-neutral-100 rounded-xl p-3 hover:border-neutral-200 transition-colors duration-200"
                        >
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-[#d4af37] to-[#b8941e] flex items-center justify-center shadow-sm">
                                <span className="text-white text-[10px] font-semibold">
                                  {comment._commentby 
                                    ? `${comment._commentby.first_name[0]}${comment._commentby.last_name[0]}`
                                    : 'U'}
                                </span>
                              </div>
                              <div>
                                <p className="font-medium text-xs text-neutral-900">
                                  {comment._commentby 
                                    ? `${comment._commentby.first_name} ${comment._commentby.last_name}`
                                    : 'Unknown User'}
                                </p>
                                <div className="flex items-center gap-1 text-[10px] text-neutral-400 mt-0.5">
                                  <Clock className="w-2.5 h-2.5" />
                                  <span>{formatDisplayDate(comment.created_at)}</span>
                                </div>
                              </div>
                            </div>
                          </div>
                          <p className="text-xs text-neutral-600 leading-relaxed whitespace-pre-wrap">
                            {comment.message || 'No comment text'}
                          </p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-6 bg-neutral-50 rounded-xl border border-neutral-100 mb-3">
                      <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center mx-auto mb-2 border border-neutral-100">
                        <MessageSquare className="w-5 h-5 text-neutral-300" />
                      </div>
                      <p className="text-xs text-neutral-400">No comments yet</p>
                    </div>
                  )}

                  {/* Add Comment Input */}
                  <div className="border-t border-neutral-100 pt-3">
                    <Label className="text-xs font-semibold text-neutral-400 uppercase tracking-wider mb-2 block">
                      Add Comment
                    </Label>
                    <div className="flex gap-2">
                      <Textarea
                        placeholder="Write your comment here..."
                        value={newComment}
                        onChange={(e) => setNewComment(e.target.value)}
                        className="rounded-xl resize-none text-sm"
                        rows={2}
                      />
                    </div>
                    <div className="flex justify-end mt-2">
                      <Button
                        onClick={handleAddComment}
                        disabled={addingComment || !newComment.trim()}
                        className="bg-gradient-to-r from-[#d4af37] to-[#b8941e] hover:from-[#b8941e] hover:to-[#d4af37] text-white rounded-xl shadow-sm text-sm h-8"
                      >
                        {addingComment ? (
                          <>
                            <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                            Posting...
                          </>
                        ) : (
                          <>
                            <Send className="w-3.5 h-3.5 mr-1.5" />
                            Post Comment
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-2xl border border-neutral-100 overflow-hidden shadow-sm">
              <div className="p-12">
                <div className="text-center">
                  <div className="w-16 h-16 bg-neutral-50 rounded-xl flex items-center justify-center mx-auto mb-4 border border-neutral-100">
                    <MessageSquare className="w-8 h-8 text-neutral-300" />
                  </div>
                  <p className="text-neutral-400 text-sm">Select a ticket to view details</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Create Ticket Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="text-2xl font-semibold">Create New Ticket</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="subject" className="text-sm font-medium">
                Subject <span className="text-red-500">*</span>
              </Label>
              <Input
                id="subject"
                placeholder="Brief description of your issue"
                value={newTicket.name}
                onChange={(e) => setNewTicket({ ...newTicket, name: e.target.value })}
                className="rounded-xl"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="description" className="text-sm font-medium">
                Description
              </Label>
              <Textarea
                id="description"
                placeholder="Provide detailed information about your issue..."
                value={newTicket.description}
                onChange={(e) => setNewTicket({ ...newTicket, description: e.target.value })}
                className="rounded-xl min-h-[150px]"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowCreateModal(false);
                setNewTicket({ name: '', description: '' });
              }}
              className="rounded-xl"
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateTicket}
              disabled={!newTicket.name.trim()}
              className="bg-gradient-to-r from-[#d4af37] to-[#b8941e] hover:from-[#b8941e] hover:to-[#d4af37] text-white rounded-xl shadow-sm"
            >
              <Plus className="w-4 h-4 mr-2" />
              Continue
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Summary/Confirmation Modal */}
      <Dialog open={showSummaryModal} onOpenChange={setShowSummaryModal}>
        <DialogContent className="sm:max-w-[550px]">
          <DialogHeader>
            <DialogTitle className="text-2xl font-semibold">Review Your Ticket</DialogTitle>
            <p className="text-sm text-neutral-500 mt-2">Please review the details before submitting</p>
          </DialogHeader>
          <div className="space-y-6 py-6">
            {/* Subject */}
            <div className="space-y-2">
              <Label className="text-xs font-semibold text-neutral-500 uppercase tracking-wider">Subject</Label>
              <div className="bg-neutral-50 rounded-xl p-4 border border-neutral-100">
                <p className="text-sm font-medium text-neutral-900">{newTicket.name}</p>
              </div>
            </div>
            
            {/* Description */}
            <div className="space-y-2">
              <Label className="text-xs font-semibold text-neutral-500 uppercase tracking-wider">Description</Label>
              <div className="bg-neutral-50 rounded-xl p-4 border border-neutral-100 max-h-[200px] overflow-y-auto">
                <p className="text-sm text-neutral-700 whitespace-pre-wrap">
                  {newTicket.description || 'No description provided'}
                </p>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowSummaryModal(false);
                setShowCreateModal(true);
              }}
              disabled={creating}
              className="rounded-xl"
            >
              Back to Edit
            </Button>
            <Button
              onClick={handleConfirmCreate}
              disabled={creating}
              className="bg-gradient-to-r from-[#d4af37] to-[#b8941e] hover:from-[#b8941e] hover:to-[#d4af37] text-white rounded-xl shadow-sm"
            >
              {creating ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4 mr-2" />
                  Confirm & Create
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Edit Ticket Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="text-2xl font-semibold">Edit Ticket</DialogTitle>
            <p className="text-sm text-neutral-500 mt-2">Update the ticket details</p>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="edit-subject" className="text-sm font-medium">
                Subject <span className="text-red-500">*</span>
              </Label>
              <Input
                id="edit-subject"
                placeholder="Brief description of your issue"
                value={editFormData.name}
                onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                className="rounded-xl"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-description" className="text-sm font-medium">
                Description
              </Label>
              <Textarea
                id="edit-description"
                placeholder="Provide detailed information about your issue..."
                value={editFormData.description}
                onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })}
                className="rounded-xl min-h-[150px]"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowEditModal(false);
                setEditingTicket(null);
                setEditFormData({ name: '', description: '' });
              }}
              disabled={updating}
              className="rounded-xl"
            >
              Cancel
            </Button>
            <Button
              onClick={handleUpdateTicket}
              disabled={updating || !editFormData.name.trim()}
              className="bg-gradient-to-r from-[#d4af37] to-[#b8941e] hover:from-[#b8941e] hover:to-[#d4af37] text-white rounded-xl shadow-sm"
            >
              {updating ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Updating...
                </>
              ) : (
                <>
                  <Edit className="w-4 h-4 mr-2" />
                  Update Ticket
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
